import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar, LogIn, LogOut, MinusCircle, XCircle } from 'lucide-react'; // Import new icons
import { getRequest } from '../../services/apiService';

// Modified to accept selectedProjectId as a prop
const ShiftSchedule = ({ selectedProjectId }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date()); // Initialize with current date
  const [selectedWeekStart, setSelectedWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when Sunday (0)
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0); // Normalize to start of day
    return monday;
  });
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  // Initialize projectId with the prop or a default value
  const [projectId, setProjectId] = useState(selectedProjectId || 1);

  // Pagination states
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [previousPageUrl, setPreviousPageUrl] = useState(null);

  const [editForm, setEditForm] = useState({
    startTime: '',
    endTime: '',
    notes: ''
  });

  const calendarRef = useRef(null);
  const [draggingShiftId, setDraggingShiftId] = useState(null);

  // Removed generateLightColor function as color will come from API

  const fetchShiftData = useCallback(async (pageUrl = null) => {
    let apiUrl;
    if (pageUrl) {
      // Use the provided pageUrl for pagination
      apiUrl = pageUrl;
    } else {
      // Construct the URL based on selectedWeekStart and projectId for initial load
      const startDateFormatted = selectedWeekStart.toLocaleDateString('en-GB').split('/').join('-');
      const endDate = new Date(selectedWeekStart);
      endDate.setDate(selectedWeekStart.getDate() + 6); // Fetch for 7 days
      const endDateFormatted = endDate.toLocaleDateString('en-GB').split('/').join('-');
      apiUrl = `/config/employ-shift/?start_date=${startDateFormatted}&end_date=${endDateFormatted}&project_id=${projectId}`;
    }


    try {
      const response = await getRequest(apiUrl);
      const apiData = response.results;

      // Update pagination URLs
      setNextPageUrl(response.next);
      setPreviousPageUrl(response.previous);

      const newStaff = [];
      const newShifts = [];

      apiData.forEach(userShiftData => {
        const staffMember = {
          id: userShiftData.user_id,
          name: userShiftData.login_name,
          color: userShiftData.color, // Use color from API response
          title: userShiftData.role || 'N/A', // Use role as title, or a default
          // No img property as per user request to not show images
        };
        newStaff.push(staffMember);

        userShiftData.shifts.forEach((shift, index) => {
          const shiftId = `${userShiftData.user_id}-${shift.attendance_date}-${index}`; // Unique ID for each shift
          const attendanceDate = shift.attendance_date; // Assuming attendance_date is like "13-06-2025"

          let checkInTime = shift.check_in ? new Date(shift.check_in).toTimeString().substring(0, 5) : 'Not Marked';
          let checkOutTime = shift.check_out ? new Date(shift.check_out).toTimeString().substring(0, 5) : 'Not Marked';
          let displayTitle = '';
          let isAbsent = false;

          // Logic for display based on check_in and check_out presence
          if (!shift.check_in && !shift.check_out) {
            isAbsent = true;
            displayTitle = shift.attendance_status || 'Absent'; // Use attendance_status if both are null
            checkInTime = displayTitle; // Set both display times to "Absent" for uniform cell display
            checkOutTime = displayTitle;
          } else if (shift.check_in && !shift.check_out) {
            displayTitle = `${checkInTime} - Not Marked`;
          } else if (!shift.check_in && shift.check_out) {
            displayTitle = `Not Marked - ${checkOutTime}`;
          } else {
            displayTitle = `${checkInTime} - ${checkOutTime}`;
          }

          newShifts.push({
            id: shiftId,
            start: shift.check_in || null, // Store original ISO string or null for API use
            end: shift.check_out || null,   // Store original ISO string or null for API use
            title: displayTitle,           // This is the main display text for the button/cell
            resource: userShiftData.user_id,
            notes: shift.attendance_status || '', // Keeping original attendance_status in notes
            attendanceDate: attendanceDate, // Store the attendance date string directly
            checkInDisplay: checkInTime,    // Formatted check-in for UI display
            checkOutDisplay: checkOutTime,  // Formatted check-out for UI display
            isAbsent: isAbsent,             // Flag to easily check if the shift is an "Absent" entry
          });
        });
      });

      setStaff(newStaff);
      setShifts(newShifts);

      // Console log the processed data
      console.log('--- Shift Data for Table Display ---');
      console.log('Staff Data:', newStaff);
      console.log('Shifts Data (processed for display):', newShifts);
      console.log('------------------------------------');

    } catch (error) {
      console.error('Error fetching shift data:', error);
      // Handle error (e.g., show an error message to the user)
    }
  }, [selectedWeekStart, projectId]);

  useEffect(() => {
    fetchShiftData();
  }, [fetchShiftData]);

  // Add a useEffect to update projectId when selectedProjectId prop changes
  useEffect(() => {
    if (selectedProjectId) {
      setProjectId(selectedProjectId);
    }
  }, [selectedProjectId]);

  const handleNextPage = () => {
    if (nextPageUrl) {
      fetchShiftData(nextPageUrl);
    }
  };

  const handlePreviousPage = () => {
    if (previousPageUrl) {
      fetchShiftData(previousPageUrl);
    }
  };


  // Generate dates for the week based on selected week
  const generateWeekDates = () => {
    const dates = [];
    const startDate = new Date(selectedWeekStart);

    for (let i = 0; i < 5; i++) { // Display 5 days (Monday to Friday)
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Helper function to get Monday of the week for any given date
  const getMondayOfWeek = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when Sunday (0)
    const monday = new Date(date);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0); // Normalize to start of day
    return monday;
  };

  const weekDates = generateWeekDates();

  // Generate calendar dates
  const generateCalendarDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const dates = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Helper function to get day name
  const getDayName = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };


  // Helper function to check if date is today
  const isToday = (date) => {
    const today = new Date(); // Use current date for 'today'
    return date.toDateString() === today.toDateString();
  };

  // Helper function to get shifts for a specific staff member and date
  const getShiftsForStaffAndDate = (staffId, date) => {
    // Format the date to match the attendance_date format from API (e.g., "DD-MM-YYYY")
    const apiDateStr = date.toLocaleDateString('en-GB').split('/').join('-');

    return shifts.filter(shift => {
      // Use the stored attendanceDate property for filtering
      return shift.resource === staffId && shift.attendanceDate === apiDateStr;
    });
  };


  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha = 0.3) => {
    if (!hex || hex.length !== 7) return `rgba(0,0,0,${alpha})`; // Default if hex is invalid
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Convert 24-hour time to 12-hour format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || time24 === 'Not Marked' || time24 === 'Absent') return time24; // Keep "Not Marked" or "Absent" as is
    const [hours, minutes] = time24.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Convert 12-hour time to 24-hour format
  const formatTimeTo24 = (time12) => {
    // Handle "Not Marked" or "Absent"
    if (time12 === 'Not Marked' || time12 === 'Absent' || !time12) return null;

    const [time, ampm] = time12.split(' ');
    let [hours, minutes] = time.split(':');
    hours = parseInt(hours);

    if (ampm === 'PM' && hours < 12) {
      hours += 12;
    } else if (ampm === 'AM' && hours === 12) { // Midnight 12 AM
      hours = 0;
    }
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  };

  // Handle shift click
  const handleShiftClick = (shift, staffMember, date) => {
    setSelectedShift({
      ...shift,
      staffMember,
      date,
      // Use display properties for modal's initial values
      startTime: shift.checkInDisplay,
      endTime: shift.checkOutDisplay,
    });

    setEditForm({
      startTime: formatTimeToAMPM(shift.checkInDisplay),
      endTime: formatTimeToAMPM(shift.checkOutDisplay),
      notes: shift.notes || ''
    });
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev, // Correctly spread previous state
      [field]: value
    }));
  };

  // Handle save shift
  const handleSaveShift = () => {
    if (!selectedShift) return;

    const startTime24 = formatTimeTo24(editForm.startTime); // Will be null if "Not Marked" or "Absent"
    const endTime24 = formatTimeTo24(editForm.endTime);   // Will be null if "Not Marked" or "Absent"

    const updatedShifts = shifts.map(shift => {
      if (shift.id === selectedShift.id) {
        const dateStr = selectedShift.date.toISOString().split('T')[0];
        let newStart = startTime24 ? `${dateStr}T${startTime24}:00.000Z` : null;
        let newEnd = endTime24 ? `${dateStr}T${endTime24}:00.000Z` : null;

        let newDisplayTitle = '';
        let newIsAbsent = false;
        let newCheckInDisplay = startTime24 || 'Not Marked';
        let newCheckOutDisplay = endTime24 || 'Not Marked';


        if (newStart === null && newEnd === null) {
            newIsAbsent = true;
            newDisplayTitle = 'Absent';
            newCheckInDisplay = 'Absent';
            newCheckOutDisplay = 'Absent';
        } else if (newStart !== null && newEnd === null) {
            newDisplayTitle = `${newCheckInDisplay} - Not Marked`;
        } else if (newStart === null && newEnd !== null) {
            newDisplayTitle = `Not Marked - ${newCheckOutDisplay}`;
        } else {
            newDisplayTitle = `${newCheckInDisplay} - ${newCheckOutDisplay}`;
        }

        return {
          ...shift,
          start: newStart,
          end: newEnd,
          title: newDisplayTitle,
          notes: editForm.notes,
          checkInDisplay: newCheckInDisplay,
          checkOutDisplay: newCheckOutDisplay,
          isAbsent: newIsAbsent,
        };
      }
      return shift;
    });

    setShifts(updatedShifts);
    setSelectedShift(null);
    setEditForm({ startTime: '', endTime: '', notes: '' });
  };

  // Handle delete shift
  const handleDeleteShift = () => {
    if (!selectedShift) return;

    const updatedShifts = shifts.filter(shift => shift.id !== selectedShift.id);
    setShifts(updatedShifts);
    setSelectedShift(null);
    setEditForm({ startTime: '', endTime: '', notes: '' });
  };

  // Handle today button click
  const handleTodayClick = () => {
    const today = new Date();
    const mondayOfToday = getMondayOfWeek(today);
    setSelectedWeekStart(mondayOfToday);
    setCurrentDate(today);

    setTimeout(() => {
      const todayElement = document.querySelector('[data-today="true"]');
      if (todayElement) {
        todayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Handle calendar date selection
  const handleCalendarDateSelect = (date) => {
    const mondayOfSelectedWeek = getMondayOfWeek(date);
    setSelectedWeekStart(mondayOfSelectedWeek);
    setCurrentDate(date);
    setShowCalendar(false);
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calendarDates = generateCalendarDates();

  // --- Drag and Drop Handlers ---

  const handleDragStart = (e, shiftId) => {
    setDraggingShiftId(shiftId);
    e.dataTransfer.setData('text/plain', shiftId); // Set data for drag operation
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = (e, targetStaffId, targetDate, targetSlot) => {
    e.preventDefault();
    const draggedShiftId = e.dataTransfer.getData('text/plain');

    if (draggingShiftId === draggedShiftId) { // Ensure the dragged item is the one we're tracking
      const updatedShifts = shifts.map(shift => {
        if (shift.id === draggedShiftId) {
          const dateStr = targetDate.toISOString().split('T')[0];
          let newStartTime = shift.start ? new Date(shift.start).toTimeString().substring(0, 5) : '07:00'; // Default if previously null
          let newEndTime = shift.end ? new Date(shift.end).toTimeString().substring(0, 5) : '17:00'; // Default if previously null

          // Adjust times based on target slot if the original was "Not Marked" or "Absent"
          if (shift.isAbsent || shift.checkInDisplay === 'Not Marked' || shift.checkOutDisplay === 'Not Marked') {
            if (targetSlot === 1) { // Dropped on Check-in slot
              newStartTime = '07:00';
              newEndTime = '13:00';
            } else if (targetSlot === 2) { // Dropped on Check-out slot
              newStartTime = '12:00';
              newEndTime = '18:00';
            }
          }

          let newDisplayTitle = '';
          let newIsAbsent = false;
          let newCheckInDisplay = newStartTime;
          let newCheckOutDisplay = newEndTime;

          // Re-evaluate display and absent status after drop
          if (newStartTime === 'Not Marked' && newEndTime === 'Not Marked') {
              newIsAbsent = true;
              newDisplayTitle = 'Absent';
              newCheckInDisplay = 'Absent';
              newCheckOutDisplay = 'Absent';
          } else if (newStartTime !== 'Not Marked' && newEndTime === 'Not Marked') {
              newDisplayTitle = `${newCheckInDisplay} - Not Marked`;
          } else if (newStartTime === 'Not Marked' && newEndTime !== 'Not Marked') {
              newDisplayTitle = `Not Marked - ${newCheckOutDisplay}`;
          } else {
              newDisplayTitle = `${newCheckInDisplay} - ${newCheckOutDisplay}`;
          }


          return {
            ...shift,
            resource: targetStaffId,
            start: `${dateStr}T${newStartTime}:00.000Z`,
            end: `${dateStr}T${newEndTime}:00.000Z`,
            title: newDisplayTitle,
            checkInDisplay: newCheckInDisplay,
            checkOutDisplay: newCheckOutDisplay,
            isAbsent: newIsAbsent,
            attendanceDate: targetDate.toLocaleDateString('en-GB').split('/').join('-'), // Update date string
          };
        }
        return shift;
      });

      setShifts(updatedShifts);
      setDraggingShiftId(null);
    }
  };

  const getSymbol = (status, type) => {
    if (status === 'Absent') {
      return <XCircle size={16} className="text-red-500" />;
    }
    if (status === 'Not Marked') {
      return <MinusCircle size={16} className="text-gray-400" />;
    }
    if (type === 'check-in') {
      return <LogIn size={16} className="text-green-600" />;
    }
    if (type === 'check-out') {
      return <LogOut size={16} className="text-blue-600" />;
    }
    return null;
  };


  return (
    <div className="w-full bg-white relative min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between p-2 sm:p-3 lg:p-4 bg-gray-50 border-b sticky top-0 z-40">
        <div className="flex items-center gap-2 lg:gap-4">
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className="text-xl font-medium text-blue-600 hover:bg-blue-50 px-3 py-2 rounded flex items-center space-x-2"
          >
            <span>{selectedWeekStart.toLocaleDateString('en-US', {
              month: window.innerWidth < 640 ? 'short' : 'long',
              year: 'numeric'
            })}</span>
             <Calendar className="w-5 h-5" />
          </button>

          {/* Calendar Popup */}
          {showCalendar && (
            <div
              ref={calendarRef}
              className="absolute top-full left-0 sm:left-2 lg:left-4 mt-1 bg-white border rounded-lg shadow-lg z-50 p-3 lg:p-4 w-[280px] sm:w-80 lg:w-96 max-w-[calc(100vw-1rem)]"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base lg:text-lg font-semibold text-blue-500">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 p-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {calendarDates.map((date, index) => (
                  <button
                    key={index}
                    className={`p-2 text-sm rounded hover:bg-gray-100 ${
                      date.getMonth() !== currentDate.getMonth()
                        ? 'text-gray-300'
                        : 'text-gray-700'
                    } ${
                      isToday(date)
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : ''
                    } ${
                      date.toDateString() === selectedWeekStart.toDateString() ||
                      (date > selectedWeekStart && date < new Date(selectedWeekStart.getTime() + 5 * 24 * 60 * 60 * 1000) && date.getDay() !== 0 && date.getDay() !== 6)
                        ? 'bg-blue-100 text-blue-800'
                        : ''
                    }`}
                    onClick={() => handleCalendarDateSelect(date)}
                  >
                    {date.getDate()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 lg:gap-2">
          <button
            onClick={() => {
              const prevWeek = new Date(selectedWeekStart);
              prevWeek.setDate(selectedWeekStart.getDate() - 7);
              setSelectedWeekStart(prevWeek);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <ChevronLeft size={16} className="text-blue-500 sm:w-5 sm:h-5" />
          </button>
          <button
            onClick={handleTodayClick}
            className="px-2 lg:px-3 py-1 text-blue-500 text-xs sm:text-sm hover:text-blue-600 whitespace-nowrap"
          >
            Today
          </button>
          <button
            onClick={() => {
              const nextWeek = new Date(selectedWeekStart);
              nextWeek.setDate(selectedWeekStart.getDate() + 7);
              setSelectedWeekStart(nextWeek);
            }}
            className="p-1 hover:bg-gray-200 rounded"
          >
            <ChevronRight size={16} className="text-blue-500 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Symbol Legend */}
      <div className="p-2 sm:p-3 lg:p-4 bg-white border-b flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <LogIn size={16} className="text-green-600" />
          <span>Check-in</span>
        </div>
        <div className="flex items-center gap-2">
          <LogOut size={16} className="text-blue-600" />
          <span>Check-out</span>
        </div>
        {/* <div className="flex items-center gap-2">
          <MinusCircle size={16} className="text-gray-400" />
          <span>Not Marked</span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle size={16} className="text-red-500" />
          <span>Absent</span>
        </div> */}
      </div>


      {/* Calendar Layout with Fixed Staff Column */}
      <div className="flex">
        {/* Fixed Staff Column */}
        <div className="w-[200px] bg-white border-r flex-shrink-0"> {/* Increased width */}
          {/* Staff Header */}
          <div className="p-2 sm:p-3 border-b bg-gray-50">
            <div className="text-xs sm:text-sm font-medium text-gray-600">Staff</div>
          </div>

          {/* Staff List */}
          {staff.map((member) => (
            <div key={member.id} className="p-2 sm:p-3 lg:p-4 border-b hover:bg-gray-50 h-12 sm:h-16 lg:h-20 flex items-center">
              <div className="flex items-center gap-2 lg:gap-3 w-full">
                <div className="relative flex-shrink-0">
                  {/* Removed img tag as per user request */}
                  <div
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: member.color }}
                  >
                    {member.name.charAt(0)}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800 text-xs sm:text-sm truncate">{member.name}</div>
                  <div className="text-xs text-gray-500 truncate hidden sm:block">{member.title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-x-auto">
          <div className="min-w-[800px]"> {/* Increased min-width for wider columns */}
            {/* Date Headers */}
            <div className="grid grid-cols-5 border-b bg-gray-50">
              {weekDates.map((date, index) => (
                <div key={index} className="border-r" data-today={isToday(date)}>
                  <div className="p-2 sm:p-3 text-center">
                    <div
                      className={`font-medium text-xs sm:text-sm ${
                        isToday(date)
                          ? 'text-blue-600'
                          : 'text-gray-800'
                      }`}
                    >
                      <div className="block sm:hidden">
                        {date.getDate()}
                        <div className="text-xs">{getDayName(date)}</div>
                      </div>
                      <div className="hidden sm:block md:hidden">
                        {`${date.getDate()} ${getDayName(date)}`}
                      </div>
                      <div className="hidden md:block">
                        {`${date.getDate()}  ${date.toLocaleString('default', { month: 'short' })} ${getDayName(date)}`}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 border-t">
                    <div className="p-1 sm:p-2 text-center text-xs font-medium text-gray-600 border-r flex items-center justify-center gap-1">
                      <LogIn size={14} className="text-green-600" />
                      
                    </div>
                    <div className="p-1 sm:p-2 text-center text-xs font-medium text-gray-600 flex items-center justify-center gap-1">
                      <LogOut size={14} className="text-blue-600" />
                     
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Schedule Rows */}
            {staff.map((member) => (
              <div key={member.id} className="grid grid-cols-5 border-b hover:bg-gray-50">
                {weekDates.map((date, dateIndex) => {
                  const shiftsForThisDay = getShiftsForStaffAndDate(member.id, date);
                  const absentShift = shiftsForThisDay.find(s => s.isAbsent);
                  const presentShift = shiftsForThisDay.find(s => !s.isAbsent);

                  // Determine if the current date is in the future
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Normalize today's date to start of day
                  const isFutureDate = date.getTime() > today.getTime();

                  return (
                    <div key={dateIndex} className="border-r">
                      {isFutureDate ? (
                        // If it's a future date, always render blank cells
                        <div className="grid grid-cols-2 h-12 sm:h-16 lg:h-20">
                          <div className="border-r p-1 flex items-center justify-center">
                            {/* Blank for future check-in */}
                          </div>
                          <div className="p-1 flex items-center justify-center">
                            {/* Blank for future check-out */}
                          </div>
                        </div>
                      ) : absentShift ? (
                        // Modified: Render a single cell that spans the original check-in/out slots
                        <div className="flex flex-col h-12 sm:h-16 lg:h-20 justify-center items-center">
                          <div className="flex items-center justify-center px-1 py-1 rounded text-xs text-center font-medium text-red-700 bg-red-100 border border-red-300 w-full max-w-[90%]"> {/* Added w-full max-w-[90%] for better fitting */}
                            <span className="ml-1">{absentShift.title}</span> {/* This should be "Absent" */}
                          </div>
                        </div>
                      ) : (
                        // Original: If not absent and not future, display check-in and check-out times in two columns
                        <div className="grid grid-cols-2 h-12 sm:h-16 lg:h-20">
{/* Check-in Slot */}
<div
  className="border-r flex items-center justify-center p-1" // Changed from p-0 to p-1
  onDragOver={handleDragOver}
  onDrop={(e) => handleDrop(e, member.id, date, 1)}
  style={{ flexBasis: '100%', boxSizing: 'border-box' }}
>
  {presentShift && (
    <button
      key={presentShift.id + '-in'}
      onClick={() => handleShiftClick(presentShift, member, date)}
      className="w-full h-full max-w-full max-h-full flex flex-col items-center justify-center text-xs text-center font-medium text-gray-800 hover:opacity-80 transition-opacity overflow-hidden" // Added max-w-full, max-h-full, and overflow-hidden
      style={{
        backgroundColor: hexToRgba(member.color, 0.3),
        border: `1px solid ${member.color}`,
        margin: '2px', // Added small margin to prevent touching borders
        borderRadius: '4px', // Added border radius for better appearance
      }}
      title={presentShift.title}
      draggable="true"
      onDragStart={(e) => handleDragStart(e, presentShift.id)}
    >
      <div className="flex items-center gap-0.5 px-1 py-0.5 overflow-hidden"> {/* Added overflow-hidden and adjusted padding */}
        {getSymbol(presentShift.checkInDisplay, 'check-in')}
        <span className="block sm:hidden truncate">
          {presentShift.checkInDisplay !== 'Not Marked' ? presentShift.checkInDisplay.substring(0, 2) + 'h' : 'N/A'}
        </span>
        <span className="hidden sm:block truncate">
          {formatTimeToAMPM(presentShift.checkInDisplay)}
        </span>
      </div>
    </button>
  )}
</div>

{/* Check-out Slot */}
<div
  className="p-1 flex items-center justify-center" // Changed from p-0 to p-1
  onDragOver={handleDragOver}
  onDrop={(e) => handleDrop(e, member.id, date, 2)}
>
  {presentShift && (
    <button
      key={presentShift.id + '-out'}
      onClick={() => handleShiftClick(presentShift, member, date)}
      className="w-full h-full max-w-full max-h-full flex flex-col items-center justify-center text-xs text-center font-medium text-gray-800 hover:opacity-80 transition-opacity overflow-hidden" // Added max-w-full, max-h-full, and overflow-hidden
      style={{
        backgroundColor: hexToRgba(member.color, 0.3),
        border: `1px solid ${member.color}`,
        margin: '2px', // Added small margin to prevent touching borders
        borderRadius: '4px', // Added border radius for better appearance
      }}
      title={presentShift.title}
      draggable="true"
      onDragStart={(e) => handleDragStart(e, presentShift.id)}
    >
      <div className="flex items-center gap-0.5 px-1 py-0.5 overflow-hidden"> {/* Added overflow-hidden and adjusted padding */}
        {getSymbol(presentShift.checkOutDisplay, 'check-out')}
        <span className="block sm:hidden truncate">
          {presentShift.checkOutDisplay !== 'Not Marked' ? presentShift.checkOutDisplay.substring(0, 2) + 'h' : 'N/A'}
        </span>
        <span className="hidden sm:block truncate">
          {formatTimeToAMPM(presentShift.checkOutDisplay)}
        </span>
      </div>
    </button>
  )}
</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center gap-4 p-4 bg-gray-50 border-t">
        <button
          onClick={handlePreviousPage}
          disabled={!previousPageUrl}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            previousPageUrl
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          Previous
        </button>
        <button
          onClick={handleNextPage}
          disabled={!nextPageUrl}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            nextPageUrl
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          Next
        </button>
      </div>

      {/* Edit Shift Modal */}
      {selectedShift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm sm:max-w-md md:max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="flex items-center gap-3 mb-4">
                {/* Removed img tag as per user request */}
                <div
                  className="w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: selectedShift.staffMember.color }}
                >
                  {selectedShift.staffMember.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-800 truncate">
                    Edit {selectedShift.staffMember.name}'s hours
                  </h2>
                  <p className="text-gray-600 text-xs lg:text-sm">
                    {selectedShift.date.toLocaleDateString('en-US', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })} {selectedShift.isAbsent ? 'Status' : 'Shift'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift start
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white appearance-none text-sm"
                      value={editForm.startTime}
                      onChange={(e) => handleFormChange('startTime', e.target.value)}
                      disabled={selectedShift.isAbsent} // Disable if shift is "Absent"
                    >
                      <option value="Not Marked">Not Marked</option>
                      <option value="7:00 AM">7:00 AM</option>
                      <option value="8:00 AM">8:00 AM</option>
                      <option value="9:00 AM">9:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400" size={16} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift end
                  </label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white appearance-none text-sm"
                      value={editForm.endTime}
                      onChange={(e) => handleFormChange('endTime', e.target.value)}
                      disabled={selectedShift.isAbsent} // Disable if shift is "Absent"
                    >
                      <option value="Not Marked">Not Marked</option>
                      <option value="1:00 PM">1:00 PM</option>
                      <option value="2:00 PM">2:00 PM</option>
                      <option value="3:00 PM">3:00 PM</option>
                      <option value="6:00 PM">6:00 PM</option>
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 transform -translate-y-1/2 rotate-90 text-gray-400" size={16} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none text-sm"
                    rows="3"
                    placeholder="Add notes..."
                    value={editForm.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                  />
                </div>
              </div>

              <button
                onClick={handleDeleteShift}
                className="w-full mt-6 p-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
              >
                Delete shift
              </button>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setSelectedShift(null);
                    setEditForm({ startTime: '', endTime: '', notes: '' });
                  }}
                  className="flex-1 p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveShift}
                  className="flex-1 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftSchedule;